import { useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/facilities.ts'
import { facilityDefinition } from '#app/routes/mocra+/_form+/components/facilities/definition.ts'
import { FacilityEditOverlay } from '#app/routes/mocra+/_form+/components/facilities/edit.tsx'
import {
	FacilityEntrySchema,
	type FacilityEntry,
} from '#app/routes/mocra+/_form+/components/facilities/schema.ts'
import { type FacilityDraft } from '#app/routes/mocra+/_form+/components/facilities/types.ts'
import { FacilityRosterSection } from '#app/routes/mocra+/_form+/facilities/facility-roster-section.tsx'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useEntityCollection } from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

const FacilitiesSchema = z.object({
	facilities: z.array(FacilityEntrySchema).default([]),
})

export type FacilitiesFormValues = z.infer<typeof FacilitiesSchema>

const DEFAULT_VALUES: FacilitiesFormValues = {
	facilities: [],
}

const SECTION_KEY = 'facilities'
const STEP_ID = 'facilities'
const FACILITIES_RESOURCES = createRouteSectionResources({
	route: '/mocra/facilities',
})

export const meta: Route.MetaFunction = () => [{ title: 'MoCRA — Facilities' }]

export default function Facilities() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [editingIndex, setEditingIndex] = useState<number | null>(null)
	const form = useSectionForm({
		section: SECTION_KEY,
		schema: FacilitiesSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const { submit, status, hasStoredValue, clear } = form

	const facilitiesCollection = useEntityCollection<
		FacilitiesFormValues,
		FacilityEntry,
		FacilityDraft
	>({
		form,
		fieldPath: 'facilities',
		definition: facilityDefinition,
		getFieldErrorPaths: getFacilityFieldErrorPaths,
	})

	const facilityList = facilitiesCollection.items.map(({ draft, errors }) => ({
		draft,
		errors,
	}))

	const editingDraft =
		editingIndex !== null && facilitiesCollection.items[editingIndex]
			? facilitiesCollection.items[editingIndex].draft
			: facilitiesCollection.createDraft()

	const handleAddFacility = (draft: FacilityDraft) => {
		facilitiesCollection.append(draft)
	}

	const handleEditFacility = (draft: FacilityDraft) => {
		if (editingIndex === null) return
		facilitiesCollection.replace(editingIndex, draft)
		setEditingIndex(null)
	}

	const handleRemoveFacility = (index: number) => {
		facilitiesCollection.remove(index)
		setEditingIndex(null)
	}

	return (
		<Page
			title="Facilities"
			description="Document each site that manufactures, processes, or stores products so §607 registrations stay accurate."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />
				<FacilityRosterSection
					resources={FACILITIES_RESOURCES}
					facilities={facilityList}
					onAdd={handleAddFacility}
					onEdit={(index) => setEditingIndex(index)}
					onRemove={handleRemoveFacility}
					emptyState="No facilities on record yet."
				/>
				<FacilityEditOverlay
					open={editingIndex !== null}
					onOpenChange={(open) => {
						if (!open) {
							setEditingIndex(null)
						}
					}}
					onSubmit={handleEditFacility}
					initialValue={editingDraft}
					onClosed={() => setEditingIndex(null)}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}
				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function getFacilityFieldErrorPaths(index: number): readonly string[] {
	const prefix = `facilities.${index}`
	const addressPrefix = `${prefix}.address`
	return [
		prefix,
		`${prefix}.name`,
		`${prefix}.type`,
		`${prefix}.registrationStatus`,
		`${prefix}.feiStatus`,
		`${prefix}.feiNumber`,
		addressPrefix,
		`${addressPrefix}.street`,
		`${addressPrefix}.street2`,
		`${addressPrefix}.city`,
		`${addressPrefix}.state`,
		`${addressPrefix}.postalCode`,
		`${addressPrefix}.country`,
	]
}
